import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';

/** Canonical defaults — Arabic-first, aligned with billing submodules. */
export const DEFAULT_FACTURATION_CONFIG = {
  // إنشاء مستند / الفواتير / بونات التسليم
  defaultUnit: 'قطعة',
  defaultTaxRate: SAUDI_BRAND.vatRate,
  vatMode: 'exclusive',
  showTva: true,
  tableType: 'standard',
  showReferenceColumn: true,
  autoGenerateDocumentNumber: true,
  autoCalculate: true,
  requireApproval: false,
  requireClient: true,
  requireLineItems: true,
  defaultTerms: '',
  defaultNotes: '',
  invoiceTemplate: 'heritage',
  documentPrefixes: {
    quote: 'DEV',
    proforma: 'PRO',
    invoice: 'FAC',
    delivery: 'BL',
    customerReturnSlip: 'BRC',
    creditNote: 'NC',
    paymentReceipt: 'RP',
    quoteRequest: 'DD',
    proformaRequest: 'DPR',
    order: 'BC',
    supplierReturnSlip: 'BRF',
    paymentSlip: 'BV',
    simplifiedInvoice: 'FSM',
    debitNote: 'ND',
    sendSlip: 'BE',
    reception: 'BRL',
    paymentSchedule: 'ECH',
  },
  entityPrefixes: {
    client: 'CLI',
    supplier: 'FRN',
    product: 'PRD',
    service: 'SRV',
    category: 'CAT',
  },
  documentDelays: {
    quote: 15,
    proforma: 30,
    invoice: 30,
    delivery: 0,
    customerReturn: 0,
    creditNote: 0,
    paymentReceipt: 0,
    quoteRequest: 15,
    proformaRequest: 15,
    purchaseOrder: 7,
    supplierReturn: 0,
    paymentVoucher: 0,
  },
  deliveryTimeframes: {
    quoteRealizationDeadline: '',
    quoteDeliveryDelay: '',
    proformaRealizationDeadline: '',
    proformaDeliveryDelay: '',
    quoteRequest: '',
    proformaRequest: '',
    purchaseOrder: '',
  },

  // المدفوعات والأقساط
  defaultPaymentMethod: 'cash',
  defaultPaymentTermsDays: 30,
  allowPartialPayments: true,
  allowInstallments: true,
  latePaymentFee: 0,
  reminderDaysBefore: 3,

  // الذمم والمتأخرات
  overdueThresholdDays: 30,
  requireTaxNumber: false,
  requireAddress: false,
  autoAssignClientNumber: true,
  clientNumberFormat: 'CLI-{####}',
  clientDiscounts: {
    company: 0,
    individual: 0,
    institution: 0,
  },

  // متابعة التحصيل
  autoSendReminders: false,
  reminderIntervalDays: 7,
  maxReminders: 3,
  escalationAfterDays: 30,

  // العرض
  itemsPerPage: 50,
  defaultView: 'table',
};

const FRENCH_UNIT_ALIASES = new Set(['unité', 'unite', 'Unité', 'piece', 'pcs']);

function mergeNestedObjects(base = {}, patch = {}) {
  const out = { ...base };
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = mergeNestedObjects(base[key] || {}, value);
    } else if (value !== undefined) {
      out[key] = value;
    }
  });
  return out;
}

/** Flatten legacy nested API payloads (invoicing/payments/products/clients). */
function flattenLegacyFacturationConfig(raw = {}) {
  if (!raw || typeof raw !== 'object') return {};

  const flat = { ...raw };

  if (raw.invoicing && typeof raw.invoicing === 'object') {
    const inv = raw.invoicing;
    if (inv.tvaRate != null) flat.defaultTaxRate = inv.tvaRate;
    if (inv.showTva != null) flat.showTva = inv.showTva;
    if (inv.tableType) flat.tableType = inv.tableType;
    if (inv.invoiceTemplate) flat.invoiceTemplate = inv.invoiceTemplate;
    if (inv.defaultTerms != null) flat.defaultTerms = inv.defaultTerms;
    if (inv.defaultNotes != null) flat.defaultNotes = inv.defaultNotes;
    if (inv.numberFormat) flat.invoiceNumberFormat = inv.numberFormat;
    if (inv.quoteNumberFormat) flat.quoteNumberFormat = inv.quoteNumberFormat;
  }

  if (raw.payments && typeof raw.payments === 'object') {
    const pay = raw.payments;
    if (pay.defaultMethod) flat.defaultPaymentMethod = pay.defaultMethod;
    if (pay.allowPartialPayments != null) flat.allowPartialPayments = pay.allowPartialPayments;
    if (pay.allowInstallments != null) flat.allowInstallments = pay.allowInstallments;
    if (pay.latePaymentFee != null) flat.latePaymentFee = pay.latePaymentFee;
    if (pay.reminderDaysBefore != null) flat.reminderDaysBefore = pay.reminderDaysBefore;
  }

  if (raw.products && typeof raw.products === 'object') {
    const prod = raw.products;
    if (prod.defaultUnit) flat.defaultUnit = prod.defaultUnit;
    if (prod.autoGenerateSKU != null) flat.autoGenerateDocumentNumber = prod.autoGenerateSKU;
  }

  if (raw.clients && typeof raw.clients === 'object') {
    const cli = raw.clients;
    if (cli.requireTaxNumber != null) flat.requireTaxNumber = cli.requireTaxNumber;
    if (cli.requireAddress != null) flat.requireAddress = cli.requireAddress;
    if (cli.autoAssignClientNumber != null) flat.autoAssignClientNumber = cli.autoAssignClientNumber;
    if (cli.clientNumberFormat) flat.clientNumberFormat = cli.clientNumberFormat;
  }

  if (raw.moduleSettings?.configuration) {
    const cfg = raw.moduleSettings.configuration;
    if (cfg.documentPrefixes) flat.documentPrefixes = cfg.documentPrefixes;
    if (cfg.documentDelays) flat.documentDelays = cfg.documentDelays;
    if (cfg.deliveryTimeframes) flat.deliveryTimeframes = cfg.deliveryTimeframes;
    if (cfg.entityPrefixes) flat.entityPrefixes = cfg.entityPrefixes;
    if (cfg.clientDiscounts) flat.clientDiscounts = cfg.clientDiscounts;
    if (cfg.defaultPaymentMethod) flat.defaultPaymentMethod = cfg.defaultPaymentMethod;
    if (cfg.allowPartialPayments != null) flat.allowPartialPayments = cfg.allowPartialPayments;
    if (cfg.allowInstallments != null) flat.allowInstallments = cfg.allowInstallments;
    if (cfg.latePaymentFee != null) flat.latePaymentFee = cfg.latePaymentFee;
    if (cfg.reminderDaysBefore != null) flat.reminderDaysBefore = cfg.reminderDaysBefore;
    if (cfg.defaultUnit) flat.defaultUnit = cfg.defaultUnit;
    if (cfg.requireTaxNumber != null) flat.requireTaxNumber = cfg.requireTaxNumber;
    if (cfg.requireAddress != null) flat.requireAddress = cfg.requireAddress;
  }

  delete flat.invoicing;
  delete flat.payments;
  delete flat.products;
  delete flat.clients;
  delete flat.moduleSettings;

  return flat;
}

/** Map legacy API keys and scrub French leftovers. */
export function normalizeFacturationConfig(raw = {}) {
  const flattened = flattenLegacyFacturationConfig(raw);
  const merged = {
    ...DEFAULT_FACTURATION_CONFIG,
    ...flattened,
    documentPrefixes: mergeNestedObjects(
      DEFAULT_FACTURATION_CONFIG.documentPrefixes,
      flattened.documentPrefixes,
    ),
    entityPrefixes: mergeNestedObjects(
      DEFAULT_FACTURATION_CONFIG.entityPrefixes,
      flattened.entityPrefixes,
    ),
    documentDelays: mergeNestedObjects(
      DEFAULT_FACTURATION_CONFIG.documentDelays,
      flattened.documentDelays,
    ),
    deliveryTimeframes: mergeNestedObjects(
      DEFAULT_FACTURATION_CONFIG.deliveryTimeframes,
      flattened.deliveryTimeframes,
    ),
    clientDiscounts: mergeNestedObjects(
      DEFAULT_FACTURATION_CONFIG.clientDiscounts,
      flattened.clientDiscounts,
    ),
  };

  if (FRENCH_UNIT_ALIASES.has(String(merged.defaultUnit || '').trim())) {
    merged.defaultUnit = DEFAULT_FACTURATION_CONFIG.defaultUnit;
  }

  if (flattened.defaultMethod && !flattened.defaultPaymentMethod) {
    merged.defaultPaymentMethod = flattened.defaultMethod;
  }

  delete merged.defaultMethod;
  delete merged.enableNotifications;
  delete merged.enablePaymentNotifications;
  delete merged.notifyPaymentDue;
  delete merged.notifyInvoiceCreated;

  const pageSize = Number(merged.itemsPerPage);
  merged.itemsPerPage = [25, 50, 100].includes(pageSize)
    ? pageSize
    : DEFAULT_FACTURATION_CONFIG.itemsPerPage;

  if (merged.defaultView !== 'table' && merged.defaultView !== 'cards') {
    merged.defaultView = DEFAULT_FACTURATION_CONFIG.defaultView;
  }

  return merged;
}

export function getFacturationDocumentPrefix(settings, docKey, fallback) {
  const fromGeneral = settings?.configuration?.documentPrefixes?.[docKey];
  if (fromGeneral) return String(fromGeneral).trim() || fallback;
  const fromFacturation = settings?.facturation?.documentPrefixes?.[docKey];
  if (fromFacturation) return String(fromFacturation).trim() || fallback;
  return fallback;
}

/** Resolved client-type discounts — live from facturation config or legacy settings bridge. */
export function getFacturationClientDiscounts(settings) {
  const fromFacturation = settings?.facturation?.clientDiscounts;
  if (fromFacturation && typeof fromFacturation === 'object') {
    return { ...DEFAULT_FACTURATION_CONFIG.clientDiscounts, ...fromFacturation };
  }
  const fromCfg = settings?.configuration?.clientDiscounts;
  if (fromCfg && typeof fromCfg === 'object') {
    return { ...DEFAULT_FACTURATION_CONFIG.clientDiscounts, ...fromCfg };
  }
  return { ...DEFAULT_FACTURATION_CONFIG.clientDiscounts };
}

/** VAT calculation mode for new documents (exclusive = HT prices, inclusive = TTC prices). */
export function getFacturationVatMode(settings) {
  return (
    settings?.facturation?.vatMode
    || settings?.invoicing?.vatMode
    || DEFAULT_FACTURATION_CONFIG.vatMode
  );
}

/** Default payment terms string from config days (short = select values, extended = PO/quote labels). */
export function resolveFacturationDefaultPaymentTerms(config = {}, { format = 'short' } = {}) {
  const days = Number(
    config?.defaultPaymentTermsDays ?? DEFAULT_FACTURATION_CONFIG.defaultPaymentTermsDays,
  );
  if (!Number.isFinite(days) || days <= 0) return 'comptant';
  if (format === 'extended') {
    if (days === 30) return '30_jours_nets';
    if (days === 45) return '45_jours_fin_mois';
    if (days === 60) return '60_jours_fin_mois';
    if (days === 90) return '90_jours';
    return `${days}_jours`;
  }
  return String(days);
}

/** Split taxable base into HT / TVA / TTC according to configured VAT mode. */
export function computeVatBreakdown(taxableBase, tvaRate, vatMode = 'exclusive') {
  const base = Number(taxableBase) || 0;
  const rate = Number(tvaRate) || 0;
  if (rate <= 0) {
    return { totalHT: base, tvaAmount: 0, totalTTC: base };
  }
  if (vatMode === 'inclusive') {
    const totalTTC = base;
    const totalHT = base / (1 + rate / 100);
    return { totalHT, tvaAmount: totalTTC - totalHT, totalTTC };
  }
  const totalHT = base;
  const tvaAmount = totalHT * (rate / 100);
  return { totalHT, tvaAmount, totalTTC: totalHT + tvaAmount };
}

/** Preferential discount % for a CRM client type (company / individual / institution). */
export function getClientTypePreferentialRate(clientType, settings) {
  if (!clientType) return 0;
  const discounts = getFacturationClientDiscounts(settings);
  const tl = String(clientType).toLowerCase();
  if (tl === 'company' || tl === 'entreprise') return Number(discounts.company) || 0;
  if (tl === 'individual' || tl === 'particulier') return Number(discounts.individual) || 0;
  if (tl === 'institution') return Number(discounts.institution) || 0;
  return 0;
}

/** Document-type → prefix map for archive conversions & PDF filenames (live config). */
export function buildFacturationConversionPrefixMap(settings) {
  const p = (docKey, fallback) => getFacturationDocumentPrefix(settings, docKey, fallback);
  return {
    invoice: p('invoice', 'FAC'),
    proforma: p('proforma', 'PRO'),
    'quote-request': p('quoteRequest', 'DD'),
    quote_request: p('quoteRequest', 'DD'),
    quote: p('quote', 'DEV'),
    'purchase-order': p('order', 'BC'),
    purchase_order: p('order', 'BC'),
    delivery: p('delivery', 'BL'),
    'delivery-note': p('delivery', 'BL'),
    delivery_note: p('delivery', 'BL'),
    'material-exit': p('delivery', 'BL'),
    material_exit: p('delivery', 'BL'),
    shipping: p('sendSlip', 'BE'),
    'shipping-slip': p('sendSlip', 'BE'),
    shipping_slip: p('sendSlip', 'BE'),
    'proforma-request': p('proformaRequest', 'DPR'),
    proforma_request: p('proformaRequest', 'DPR'),
    'reception-note': p('reception', 'BRL'),
    reception_note: p('reception', 'BRL'),
    'receipt-note': p('reception', 'BRL'),
    receipt_note: p('reception', 'BRL'),
    'return-slip': p('supplierReturnSlip', 'BRF'),
    return_slip: p('supplierReturnSlip', 'BRF'),
    'credit-note': p('creditNote', 'NC'),
    credit_note: p('creditNote', 'NC'),
    'payment-receipt': p('paymentReceipt', 'RP'),
    payment_receipt: p('paymentReceipt', 'RP'),
    'payment-slip': p('paymentSlip', 'BV'),
    payment_slip: p('paymentSlip', 'BV'),
    'payment-voucher': p('paymentSlip', 'BV'),
    payment_voucher: p('paymentSlip', 'BV'),
    'exit-slip': p('exitSlip', 'BS'),
    exit_slip: p('exitSlip', 'BS'),
    'exit-note': p('exitSlip', 'BS'),
    exit_note: p('exitSlip', 'BS'),
    'outbound-slip': p('exitSlip', 'BS'),
    outbound_slip: p('exitSlip', 'BS'),
    'transfer-slip': p('transferSlip', 'BT'),
    transfer_slip: p('transferSlip', 'BT'),
    'transfer-note': p('transferSlip', 'BT'),
    transfer_note: p('transferSlip', 'BT'),
    'stock-entry': p('delivery', 'EN'),
    stock_entry: p('delivery', 'EN'),
    'client-return-slip': p('customerReturnSlip', 'BRC'),
    client_return_slip: p('customerReturnSlip', 'BRC'),
  };
}

export function getFacturationDocumentDelay(settings, delayKey, fallback) {
  const fromFacturation = settings?.facturation?.documentDelays?.[delayKey];
  if (fromFacturation != null && Number.isFinite(Number(fromFacturation))) {
    return Number(fromFacturation);
  }
  const fromCfg = settings?.configuration?.documentDelays?.[delayKey];
  if (fromCfg != null && Number.isFinite(Number(fromCfg))) return Number(fromCfg);
  return fallback;
}

export function getFacturationItemsPerPage(settings) {
  const n = Number(settings?.facturation?.itemsPerPage);
  return [25, 50, 100].includes(n) ? n : DEFAULT_FACTURATION_CONFIG.itemsPerPage;
}
